// packages/core/src/learning/school-modules/anti-fragility.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Anti-fragility". surface: 'school',
// homeSchool: 'anti-fragility'. module id 'school-anti-fragility' matches the
// exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #1f3a5c) — flag for the presentation-tuning pass.
// tier/pillar: resilience / risk-mindset lens (SchoolMeta bucket
// 'macro-wealth-perspectives') → tier 'growth', pillar 'money-psychology-core'
// (per the protocol default: psychology/mindset lenses → growth /
// money-psychology-core). The content is a way of thinking about risk and
// resilience rather than a stability-protection product, so psychology-core is
// the closest fit. Flag for the presentation-tuning pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_ANTI_FRAGILITY_MODULE: LearningModule = {
	id: 'school-anti-fragility',
	title: 'Anti-fragility',
	description:
		'The lens that designs a money life to survive — and sometimes improve from — shocks: buffers, small bets with limited downside, and optionality, without mistaking chaos for strategy.',
	emoji: '🛡️', // PLACEHOLDER — tune in presentation pass
	accentColor: '#1f3a5c', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 0,
	surface: 'school',
	homeSchool: 'anti-fragility',
	lessons: [
		{
			id: 'school-anti-fragility-01',
			title: 'Fragile plans need perfect weather',
			description:
				'Learn why Anti fragility looks for the parts of a money plan that break when life gets messy.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['anti-fragility'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['fragile-planning', 'stress-exposure', 'failure-points'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['fragile planning', 'stress exposure', 'failure points'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A plan can look smart when everything goes right. The real test is what happens when income drops, rent rises, the car breaks, a family need appears, or your energy disappears.',
				},
				{
					type: 'paragraph',
					text: 'Anti fragility believes fragile plans depend on perfect conditions. They assume the paycheck arrives, the market cooperates, the body holds up, the job stays safe, and no one needs help.',
				},
				{
					type: 'bullet_list',
					items: [
						'Fragile plans have no margin',
						'Fragile plans hide weak points',
						'Fragile plans turn small shocks into big problems',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing a plan that is efficient with a plan that is strong.',
				},
				{
					type: 'example',
					text: 'Someone sends every spare dollar toward a goal and feels disciplined. Then a $900 repair hits and there is no buffer. The goal was real, but the plan had no shock absorber.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that stronger plans can look slower. Keeping cash, leaving space, and avoiding maximum pressure may feel less exciting than pushing every dollar as hard as possible. But the extra room can keep the plan alive.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens starts with Dashboard and runway. Look for the part of your plan that only works if nothing changes. That is the weak point.',
				},
				{
					type: 'key_takeaway',
					text: 'A plan is not strong because it looks good in calm weather. It is strong when it can absorb stress.',
				},
			],
			audioScript:
				'Anti fragility starts by asking an uncomfortable question: what part of your plan needs everything to go right? That question matters because real life does not move in a clean straight line. Income changes. Jobs change. Rent changes. Cars break. Health changes. Family needs appear. Markets move. Motivation drops. Sometimes the thing that breaks the plan is not dramatic. It is one boring surprise at the wrong time.\n\nA fragile plan can look impressive when life is calm. You might have every dollar assigned, every goal ranked, every payment timed, and every projection looking clean. On paper, it feels responsible. But if there is no room for error, the plan may be more delicate than it looks. It can be efficient and still be fragile.\n\nThink about someone who is trying to get ahead. They send every extra dollar toward debt, investing, or a big goal. That discipline is not fake. It is admirable. But if they have no buffer at all, a normal surprise can push them backward. A car repair, medical bill, family emergency, or missed paycheck can force new debt or panic decisions. The problem was not that the goal was bad. The problem was that the plan had no shock absorber.\n\nAnti fragility does not say never push. It says know what kind of pressure your system can handle. There is a difference between stretching and snapping. A stronger plan leaves some space for reality. It may keep cash that looks idle. It may avoid a payment that feels barely affordable. It may build runway before making a risky move. It may choose a slower path because the faster path fails too easily.\n\nThe hard part is that strong plans can look less exciting. Maximum efficiency feels bold. Every dollar invested, every bill minimized, every risk taken in the name of speed. But life punishes plans that have no room for friction. Sometimes the boring buffer is the reason you survive the next surprise without losing momentum.\n\nInside Nisk, this lesson points to your current picture. Look at Dashboard. Look at your runway. Ask what would happen if income dropped for one month. Ask what would happen if a cost rose by $300. Ask what would happen if your energy was lower for a season. Not to scare yourself. To see the weak points before they become breaks.\n\nAnti fragility is not pessimism. It is respect for reality. A strong plan does not need perfect weather. It has enough structure to handle some rain.',
			flashcards: [
				{
					id: 'school-anti-fragility-01-fc-1',
					role: 'main_idea',
					front: 'What makes a plan fragile?',
					back: 'It depends on perfect conditions and has little room for surprise.',
				},
				{
					id: 'school-anti-fragility-01-fc-2',
					role: 'watch_out',
					front: 'What can efficiency hide?',
					back: 'A plan may look optimized but break under normal stress.',
				},
				{
					id: 'school-anti-fragility-01-fc-3',
					role: 'use_it',
					front: 'What should you check?',
					back: 'The part of your plan that fails if one thing changes.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Find the fragile spot',
			},
			revision: 1,
		},
		{
			id: 'school-anti-fragility-02',
			title: 'Buffers are not wasted',
			description:
				'Learn why Anti fragility treats cash, slack, and redundancy as protection against forced decisions.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['anti-fragility'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['financial-buffers', 'redundancy', 'forced-decisions'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['financial buffers', 'redundancy', 'forced decisions'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A buffer can feel boring when nothing is going wrong. That is exactly why people underestimate it.',
				},
				{
					type: 'paragraph',
					text: 'Anti fragility believes buffers are not wasted space. Cash, time, insurance, backup plans, and extra capacity can keep a surprise from becoming a crisis.',
				},
				{
					type: 'bullet_list',
					items: [
						'Cash buys time',
						'Slack protects judgment',
						'Redundancy keeps one failure from becoming everything',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating every unused dollar as lazy.',
				},
				{
					type: 'example',
					text: 'Two people lose a job. One has no runway and has to accept the first option fast. The other has three months of breathing room and can choose more carefully. The buffer did not make the problem fun. It made it less controlling.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that buffers can feel slow. Money held for safety is money not chasing growth. But safety can protect future growth by preventing desperate choices.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens points directly to runway. The question is not whether a buffer looks impressive. The question is what it lets you avoid when pressure arrives.',
				},
				{
					type: 'key_takeaway',
					text: 'A buffer is not dead money. It is time, calm, and choice stored in advance.',
				},
			],
			audioScript:
				'Buffers are easy to disrespect when life is calm. Cash sitting in an account can feel lazy. Extra time in a schedule can feel inefficient. Insurance can feel annoying. A backup plan can feel unnecessary. Redundancy can feel like overkill. Then something goes wrong, and suddenly the boring buffer becomes the most valuable part of the plan.\n\nAnti fragility believes buffers are not wasted. They are what keep small problems from becoming life changing problems. A cash buffer can keep a car repair off a credit card. A time buffer can keep one delay from ruining the whole day. A skill buffer can make a job loss less terrifying. A social buffer can mean someone can help you think clearly when stress hits. A health buffer can help you recover faster. Different buffers protect different parts of life.\n\nThe key idea is forced decisions. When you have no buffer, life can force your hand. You may take a bad job because you have no runway. You may borrow at a painful rate because the bill is due. You may stay in a bad living situation because moving requires cash. You may sell something at the wrong time because you need money now. You may say yes to something you would normally reject because pressure is high.\n\nNow compare two people who lose a job. Neither person wanted it. Neither person is happy. But one has no runway and must accept the first option that appears. The other has three months of breathing room. That second person still has stress, but they have more room to think, apply, negotiate, rest, and choose. The buffer did not remove the problem. It changed the power dynamic.\n\nThis is why buffers matter in money. They convert panic into choice. They turn emergencies into problems. They make you less dependent on perfect timing. They protect your future self from having to make big decisions in a stressed state.\n\nThe tradeoff is real. Money held in safety is not being used somewhere else. A larger buffer might slow debt payoff or investing for a while. It may feel less exciting than chasing growth. Anti fragility does not deny that cost. It says the cost may be worth it when the alternative is fragility.\n\nInside Nisk, runway is the cleanest way to see this. How many months could your current money picture support you if income changed? What expenses make the runway shorter? What choices would become easier if the runway grew? This is not about hoarding cash forever. It is about knowing what kind of shock your life can absorb.\n\nA buffer is not dead money. It is stored calm. It is stored time. It is stored refusal power. It lets you say no when a desperate yes would hurt you.',
			flashcards: [
				{
					id: 'school-anti-fragility-02-fc-1',
					role: 'main_idea',
					front: 'Why do buffers matter?',
					back: 'They turn shocks into manageable problems instead of forced decisions.',
				},
				{
					id: 'school-anti-fragility-02-fc-2',
					role: 'watch_out',
					front: 'What is the buffer mistake?',
					back: 'Treating all unused cash or slack as waste.',
				},
				{
					id: 'school-anti-fragility-02-fc-3',
					role: 'use_it',
					front: 'What does runway buy?',
					back: 'Time, calm, and better choices under pressure.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-anti-fragility-03',
			title: 'Small bets teach safely',
			description:
				'Learn why Anti fragility prefers experiments where the downside is limited and the learning is useful.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['anti-fragility'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['small-experiments', 'limited-downside', 'learning-upside'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'small experiments',
				'limited downside',
				'learning upside',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Not every risk is the same. Some risks can teach you. Some risks can wreck the base you need to stand on.',
				},
				{
					type: 'paragraph',
					text: 'Anti fragility believes small experiments can make you stronger when the downside is limited. You get information, practice, feedback, and optionality without betting the whole plan.',
				},
				{
					type: 'bullet_list',
					items: [
						'Keep the downside survivable',
						'Look for useful learning',
						'Let good signals earn more attention',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is calling a huge leap an experiment.',
				},
				{
					type: 'example',
					text: 'Someone wants a new income path. Quitting immediately may be too fragile. Testing a paid project on weekends, talking to customers, or building proof first gives them information without destroying their runway.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that small bets require patience. They may feel less dramatic than going all in. But drama is not the same as courage, and a survivable test can be smarter than a heroic leap.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to Simulate. Test a small upside move against your real numbers. Ask what you can learn without threatening the base.',
				},
				{
					type: 'key_takeaway',
					text: 'A good experiment can fail without taking your life with it.',
				},
			],
			audioScript:
				'Anti fragility is often misunderstood as “take more risk.” That is not the clean version. The better version says: take risks where the downside is limited and the learning or upside can be meaningful. That difference matters.\n\nA fragile risk threatens the base. If it fails, your life gets much harder. You lose runway, damage your health, take on heavy debt, burn a relationship, or trap yourself in a commitment you cannot carry. A useful experiment is different. It lets you learn something real without putting the whole system in danger.\n\nImagine someone wants to change careers or build side income. One version is to quit the job tomorrow, announce a new identity, and hope it works. That might sound bold, but it could also be fragile if there is no runway, no proof, and no paying demand. Another version is to run a small test. Build one project. Sell one simple service. Talk to ten potential customers. Take one contract. Apply to five roles. Shadow someone. Practice the skill in public. Track what happens. That version may feel less dramatic, but it gives information.\n\nAnti fragility likes information. It likes feedback. It likes optionality. It likes moves where a small failure teaches you without ruining you. A failed weekend project might cost time and ego, but it can teach what people do not want. A failed business funded by high interest debt can damage the next several years. Those are not the same kind of risk.\n\nThe same applies to money decisions. Testing a lower spending month is different from locking into a payment you can barely afford. Testing a new city for a short stay is different from moving with no buffer and no plan. Testing a side income idea is different from building your entire budget around money that has not arrived yet.\n\nThe tradeoff is patience. Small bets can feel slow. People love the fantasy of the big leap because it creates a clean story. But life is not a movie. The big leap is sometimes right, but it should be earned by evidence, not emotion alone. A small bet lets the idea prove itself before it gets more resources.\n\nInside Nisk, this lens points to Simulate. What does the small experiment cost? What could it change? What happens if it fails? What happens if it works? Does it teach something useful? Does it preserve runway? Does it create a new option? Those questions are stronger than “Should I risk it all?”\n\nA good experiment can fail and leave you wiser. A bad risk fails and leaves you trapped. Anti fragility is about knowing the difference.',
			flashcards: [
				{
					id: 'school-anti-fragility-03-fc-1',
					role: 'main_idea',
					front: 'What is a small bet?',
					back: 'A limited experiment that teaches without threatening the whole plan.',
				},
				{
					id: 'school-anti-fragility-03-fc-2',
					role: 'watch_out',
					front: 'What is fake experimentation?',
					back: 'Taking a huge leap and calling it a test.',
				},
				{
					id: 'school-anti-fragility-03-fc-3',
					role: 'use_it',
					front: 'What should a test protect?',
					back: 'Your runway, health, base income, and ability to recover.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test a small bet',
				scenarioType: 'custom',
			},
			revision: 1,
		},
		{
			id: 'school-anti-fragility-04',
			title: 'Optionality beats one path',
			description:
				'Learn why Anti fragility values choices, backup routes, and flexible plans when the future is uncertain.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['anti-fragility'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['optionality', 'backup-routes', 'flexible-planning'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['optionality', 'backup routes', 'flexible planning'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A one path plan can feel clean. It can also become dangerous if that one path closes.',
				},
				{
					type: 'paragraph',
					text: 'Anti fragility believes options have value because the future changes. A second income path, transferable skill, emergency fund, strong network, or flexible living setup can make one failure less final.',
				},
				{
					type: 'bullet_list',
					items: [
						'Options reduce dependence',
						'Options improve timing',
						'Options let you respond instead of freeze',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing commitment with having no backup.',
				},
				{
					type: 'example',
					text: 'Someone depends on one job, one skill, one city, and one person approving their next step. Another person has runway, a growing skill, a few relationships in the field, and a backup plan. The second person is not fearless. They are less trapped.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is collecting options forever and never choosing. Optionality is useful when it supports action, not when it becomes avoidance.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens belongs in planning. Build a freedom plan that creates more paths, not just one perfect timeline.',
				},
				{
					type: 'key_takeaway',
					text: 'Options are valuable because they keep one closed door from becoming your whole future.',
				},
			],
			audioScript:
				'Optionality is one of the cleanest Anti fragility ideas. It means having more than one possible path when life changes. That sounds simple, but it can completely change how a person experiences pressure.\n\nA one path plan can feel focused. One job. One income source. One skill. One city. One relationship holding everything together. One exact timeline. One expected outcome. There is nothing wrong with focus. Focus helps people make progress. But when every part of life depends on one path staying open, the plan can become fragile.\n\nOptions reduce dependence. If you have emergency runway, you are less dependent on the next paycheck arriving perfectly. If you have a transferable skill, you are less dependent on one employer. If you have relationships in your field, you are less dependent on cold applications. If you can lower expenses for a season, you are less dependent on high income continuing without interruption. If you understand your numbers, you are less dependent on guessing.\n\nThis does not mean you need ten plans and constant backup anxiety. That can become its own problem. The goal is not to become scattered. The goal is to avoid being trapped by one failure point.\n\nTake someone who is unhappy at work. If they have no savings, no updated resume, no network, no current skills, and high fixed expenses, their choices feel narrow. Even a small workplace issue can feel huge because leaving is not realistic. Now imagine the same person with three months of runway, a stronger skill, a few conversations started, and a clear spending plan. They may still choose to stay. But staying is now more of a choice and less of a cage.\n\nOptionality also affects timing. If you have options, you can wait for a better opportunity. You can say no to a bad deal. You can test a move before committing. You can recover after a mistake. Without options, timing controls you. You take what appears because there is no room to wait.\n\nThe blind spot is using optionality to avoid commitment. Some people keep every door open and walk through none of them. They collect plans, courses, ideas, and backup routes, but never make a real move. That is not Anti fragility. That is avoidance dressed up as strategy. Options are useful because they support better action, not because they let you postpone life forever.\n\nInside Nisk, this connects to the freedom plan. A good plan is not just one perfect timeline. It creates more paths. It gives you cash options, skill options, location options, income options, and decision options. You do not need total control over the future. You need enough flexibility to respond when the future changes.\n\nOptionality is not about being scared. It is about refusing to let one closed door become your whole future.',
			flashcards: [
				{
					id: 'school-anti-fragility-04-fc-1',
					role: 'main_idea',
					front: 'What is optionality?',
					back: 'Having multiple useful paths when life changes.',
				},
				{
					id: 'school-anti-fragility-04-fc-2',
					role: 'watch_out',
					front: 'When does optionality fail?',
					back: 'When collecting options becomes avoidance instead of action.',
				},
				{
					id: 'school-anti-fragility-04-fc-3',
					role: 'use_it',
					front: 'What options matter most?',
					back: 'Runway, skills, relationships, flexible costs, and backup routes.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build more options',
			},
			revision: 1,
		},
		{
			id: 'school-anti-fragility-05',
			title: 'Chaos is not a strategy',
			description:
				'Learn where Anti fragility breaks down when people confuse resilience with recklessness.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['anti-fragility'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['reckless-risk', 'misuse-of-chaos', 'survivable-downside'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'reckless risk',
				'misuse of chaos',
				'survivable downside',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Anti fragility can sound exciting because it talks about stress, shocks, and upside from disorder. That is also why people misuse it.',
				},
				{
					type: 'paragraph',
					text: 'This lens does not mean chase chaos, ignore planning, or take risks that can ruin your base. The useful version respects uncertainty. The reckless version worships it.',
				},
				{
					type: 'bullet_list',
					items: [
						'Keep the base protected',
						'Make downside survivable',
						'Do not confuse volatility with opportunity',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using big words to justify bad risk.',
				},
				{
					type: 'example',
					text: 'Someone says a risky move is anti fragile because it could create upside. But if failure means missed rent, damaged health, high interest debt, or losing the ability to recover, it is not a smart stress test. It is fragility with better branding.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that too much safety can also make life small. Avoiding all risk can block growth. The goal is not zero risk. The goal is risk that you can survive and learn from.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a downside check. Before a bold move, ask what failure costs, what survives, and what you learn if it does not work.',
				},
				{
					type: 'key_takeaway',
					text: 'Anti fragility is not loving chaos. It is designing your life so stress does not own you.',
				},
			],
			audioScript:
				'Anti fragility is powerful, but it is easy to abuse. People hear that some systems benefit from stress, then they turn that into a license to chase chaos. They take big risks, ignore basic preparation, and call it courage. That is not the strong version of the lens. That is recklessness with smarter language.\n\nThe real version of Anti fragility respects uncertainty. It does not pretend the future is knowable. It does not assume every plan will work. It does not build a life that collapses after one surprise. But it also does not worship danger. The point is not to throw yourself into chaos and hope it makes you stronger. The point is to design your life so stress can teach you, not destroy you.\n\nA useful Anti fragile move has survivable downside. If it fails, you can recover. You may lose time, ego, a small amount of money, or a little comfort, but you do not lose the base. The base is your ability to pay essential bills, protect health, keep relationships intact, avoid destructive debt, and make another move later. Once the base is destroyed, learning becomes much more expensive.\n\nHere is a clear example. Someone wants to start a business. Testing a small offer while keeping stable income might be a good experiment. Quitting with no runway, taking on expensive debt, and assuming customers will appear might not be. If the second move fails, the person could lose housing stability, damage credit, and create pressure that limits future choices. That is not Anti fragility. That is fragility wearing a bold outfit.\n\nThe same goes for investing, moving, career changes, side income, and lifestyle decisions. A risky move is not automatically smart because it has upside. Upside matters only after you count the downside. What breaks if this fails? What remains? How long can you last? What did you learn? Can you try again? Can you shrink the bet? Can you protect the base while still testing the idea?\n\nThere is a tradeoff on the other side too. Too much safety can make life small. If you never take a chance, never learn new skills, never ask for more, never test ideas, never change environments, you may protect yourself into stagnation. Anti fragility is not about hiding from all risk. It is about choosing risk with structure.\n\nInside Nisk, this lesson should make you pause before a bold move. Open your numbers. Check runway. Simulate the change. Then ask the hard question: if this fails, am I bruised or broken? Bruised can teach. Broken can trap.\n\nAnti fragility is not loving chaos. It is building a life where chaos does not get the final vote.',
			flashcards: [
				{
					id: 'school-anti-fragility-05-fc-1',
					role: 'main_idea',
					front: 'What is the misuse?',
					back: 'Treating chaos and reckless risk as if they automatically build strength.',
				},
				{
					id: 'school-anti-fragility-05-fc-2',
					role: 'watch_out',
					front: 'What must survive?',
					back: 'Your base, including bills, health, recovery, and future options.',
				},
				{
					id: 'school-anti-fragility-05-fc-3',
					role: 'use_it',
					front: 'What question matters?',
					back: 'If this fails, am I bruised or broken?',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check the downside',
				prompt: 'Write one bold move you are considering, what failure would cost, what would survive, and how you could shrink the downside.',
			},
			revision: 1,
		},
	],
};
